/**
* @description: 积分抽奖管理
*/
<template>
    <div class="finance_box">
        <el-tabs v-model="activeTab">
            <el-tab-pane :label="t('scoreLottery.tabConfig')" name="config">
                <div class="config-form">
                    <el-form label-width="160px">
                        <el-form-item :label="t('scoreLottery.pointsPerDraw')">
                            <el-input-number v-model="configForm.points_per_draw" :min="1" :max="999999" />
                            <span class="tip">{{ t('scoreLottery.pointsTip') }}</span>
                        </el-form-item>
                        <el-form-item>
                            <el-button type="primary" @click="saveConfig">{{ t('scoreLottery.saveConfig') }}</el-button>
                        </el-form-item>
                    </el-form>
                </div>
            </el-tab-pane>
            <el-tab-pane :label="t('scoreLottery.tabPrize')" name="prize">
                <div class="prize-toolbar">
                    <el-button type="primary" icon="Plus" @click="openPrizeDialog()">{{ t('scoreLottery.addPrize') }}</el-button>
                    <span class="prob-tip" v-if="enabledProbabilitySum > 0">
                        {{ t('scoreLottery.probSum') }}{{ (enabledProbabilitySum * 100).toFixed(4) }}%
                        <template v-if="enabledProbabilitySum > 1.0001">{{ t('scoreLottery.probOver') }}</template>
                    </span>
                </div>
                <div class="prize-table">
                    <el-table :data="configForm.prizes || []" border>
                        <el-table-column type="index" :label="t('table.index')" width="60" />
                        <el-table-column prop="sort_order" :label="t('scoreLottery.colSort')" width="70" />
                        <el-table-column prop="level" :label="t('scoreLottery.colLevel')" width="80" />
                        <el-table-column prop="level_name" :label="t('scoreLottery.colLevelName')" />
                        <el-table-column prop="prize_name" :label="t('scoreLottery.colPrizeName')" />
                        <el-table-column prop="probability" :label="t('scoreLottery.colProbability')" width="120">
                            <template #default="{ row }">
                                {{ (Number(row.probability) * 100).toFixed(4) }}%
                            </template>
                        </el-table-column>
                        <el-table-column prop="status" :label="t('scoreLottery.colStatus')" width="80">
                            <template #default="{ row }">
                                {{ row.status === 1 ? t('platformAdmin.enable') : t('platformAdmin.disable') }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="t('common.operation')" width="160" fixed="right">
                            <template #default="{ row }">
                                <el-button type="primary" link @click="openPrizeDialog(row)">{{ t('common.edit') }}</el-button>
                                <el-button type="danger" link @click="deletePrize(row)">{{ t('common.delete') }}</el-button>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
            </el-tab-pane>
        </el-tabs>
        <!-- 奖品新增/编辑弹窗 -->
        <el-dialog v-model="prizeDialogVisible" :title="prizeDialogTitle" width="440" @close="prizeDialogVisible = false">
            <el-form ref="prizeFormRef" :model="prizeForm" :rules="prizeRules" label-width="100px">
                <el-form-item :label="t('scoreLottery.colLevel')" prop="level">
                    <el-input-number v-model="prizeForm.level" :min="1" :max="999" />
                </el-form-item>
                <el-form-item :label="t('scoreLottery.colLevelName')" prop="level_name">
                    <el-input v-model="prizeForm.level_name" :placeholder="t('scoreLottery.levelNamePh')" />
                </el-form-item>
                <el-form-item :label="t('scoreLottery.colPrizeName')" prop="prize_name">
                    <el-input v-model="prizeForm.prize_name" :placeholder="t('scoreLottery.prizeNamePh')" />
                </el-form-item>
                <el-form-item :label="t('scoreLottery.winProbability')" prop="probability">
                    <el-input-number v-model="prizeForm.probability" :min="0" :max="1" :step="0.0001" :precision="4" />
                    <span class="tip">{{ t('scoreLottery.probHint') }}</span>
                </el-form-item>
                <el-form-item :label="t('scoreLottery.colSort')">
                    <el-input-number v-model="prizeForm.sort_order" :min="0" :max="9999" />
                </el-form-item>
                <el-form-item :label="t('scoreLottery.colStatus')">
                    <el-switch v-model="prizeForm.status" :active-value="1" :inactive-value="0"
                        :active-text="t('platformAdmin.enable')" :inactive-text="t('platformAdmin.disable')" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="prizeDialogVisible = false">{{ t('common.cancel') }}</el-button>
                <el-button type="primary" @click="savePrize">{{ t('common.save') }}</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { onMounted, reactive, ref, nextTick, computed } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { adminConfigApi, adminConfigSaveApi, adminPrizeSaveApi, adminPrizeDeleteApi } from "@/api/score/lottery.js";
import { usePageI18n } from "@/composables/usePageI18n.js";
import { applyScoreLotteryPrizeRules } from "@/utils/scoreListI18n.js";

const applyLabels = () => {
    applyScoreLotteryPrizeRules(prizeRules);
};
const { t } = usePageI18n(applyLabels);

const activeTab = ref('config');
const configForm = reactive({
    points_per_draw: 200,
    prizes: []
});

const prizeDialogVisible = ref(false);
const prizeDialogTitle = ref('');
const prizeFormRef = ref(null);
const prizeForm = reactive({
    id: null,
    level: 1,
    level_name: '',
    prize_name: '',
    probability: 0,
    sort_order: 0,
    status: 1
});

const prizeRules = {
    level: [{ required: true, message: '', trigger: 'blur' }],
    prize_name: [{ required: true, message: '', trigger: 'blur' }],
    probability: [{ required: true, message: '', trigger: 'blur' }]
};

const enabledProbabilitySum = computed(() => {
    return (configForm.prizes || [])
        .filter((p) => p.status === 1)
        .reduce((sum, p) => sum + (Number(p.probability) || 0), 0);
});

const resetPrizeForm = () => {
    const prizes = configForm.prizes || [];
    const maxLevel = prizes.reduce((m, p) => Math.max(m, Number(p.level) || 0), 0);
    const maxSort = prizes.reduce((m, p) => Math.max(m, Number(p.sort_order) || 0), 0);
    prizeForm.id = null;
    prizeForm.level = maxLevel + 1;
    prizeForm.level_name = '';
    prizeForm.prize_name = '';
    prizeForm.probability = 0;
    prizeForm.sort_order = maxSort + 1;
    prizeForm.status = 1;
};

const loadConfig = () => {
    adminConfigApi(JSON.stringify({})).then(res => {
        if (res.code === 200 && res.data) {
            configForm.points_per_draw = res.data.points_per_draw ?? 200;
            configForm.prizes = res.data.prizes ?? [];
        }
    });
};

const saveConfig = () => {
    if (!configForm.points_per_draw || configForm.points_per_draw < 1) {
        ElMessage.warning(t('scoreLottery.pointsMinWarning'));
        return;
    }
    adminConfigSaveApi(JSON.stringify({ points_per_draw: configForm.points_per_draw })).then(res => {
        if (res.code === 200) {
            ElMessage.success(t('systemSetting.saveSuccess'));
        } else {
            ElMessage.error(res.msg || t('systemSetting.saveFailed'));
        }
    });
};

const openPrizeDialog = (row) => {
    if (row && row.id) {
        prizeDialogTitle.value = t('scoreLottery.editPrize');
        prizeForm.id = row.id;
        prizeForm.level = row.level;
        prizeForm.level_name = row.level_name || '';
        prizeForm.prize_name = row.prize_name || '';
        prizeForm.probability = Number(row.probability) || 0;
        prizeForm.sort_order = row.sort_order ?? 0;
        prizeForm.status = row.status ?? 1;
    } else {
        prizeDialogTitle.value = t('scoreLottery.addPrizeTitle');
        resetPrizeForm();
    }
    prizeDialogVisible.value = true;
    nextTick(() => prizeFormRef.value?.clearValidate?.());
};

const savePrize = () => {
    prizeFormRef.value?.validate((valid) => {
        if (!valid) return;
        const payload = {
            id: prizeForm.id || undefined,
            level: prizeForm.level,
            level_name: prizeForm.level_name,
            prize_name: prizeForm.prize_name,
            probability: prizeForm.probability,
            sort_order: prizeForm.sort_order,
            status: prizeForm.status
        };
        adminPrizeSaveApi(JSON.stringify(payload)).then(res => {
            if (res.code === 200) {
                ElMessage.success(t('systemSetting.saveSuccess'));
                prizeDialogVisible.value = false;
                loadConfig();
            } else {
                ElMessage.error(res.msg || t('systemSetting.saveFailed'));
            }
        });
    });
};

const deletePrize = (row) => {
    ElMessageBox.confirm(
        t('scoreLottery.deleteConfirm', { name: row.prize_name || row.level_name || '' }),
        t('scoreLottery.deleteTitle'),
        { type: 'warning' }
    ).then(() => {
        adminPrizeDeleteApi(JSON.stringify({ id: row.id })).then(res => {
            if (res.code === 200) {
                ElMessage.success(t('scoreLottery.deleteSuccess'));
                loadConfig();
            } else {
                ElMessage.error(res.msg || t('scoreLottery.deleteFailed'));
            }
        });
    }).catch(() => {});
};

onMounted(() => {
    applyLabels();
    loadConfig();
});
</script>

<style lang="less" scoped>
.finance_box {
    flex: 1;
    overflow: auto;
    background-color: #fff;
    border-radius: 6px;
    padding: 10px;
    margin-right: 10px;
}
.config-form {
    padding: 20px;
}
.prize-toolbar {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 12px;
}
.prize-table {
    padding: 0 0 10px;
}
.prob-tip {
    font-size: 13px;
    color: #666;
}
.tip {
    margin-left: 8px;
    font-size: 12px;
    color: #999;
}
</style>
